import { useEffect, useState } from "react";
import { Hourglass, PartyPopper } from "lucide-react";

const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const fmtMonth = (d) => d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

function useTick(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = Math.max(0, (target ? new Date(target).getTime() : now) - now);
  return { d: Math.floor(ms / 864e5), h: Math.floor(ms / 36e5) % 24, m: Math.floor(ms / 6e4) % 60, s: Math.floor(ms / 1e3) % 60 };
}

export function LuckyCountdown({ ld, active, renewalDate }) {
  const left = ld.months_remaining;
  const total = ld.eligibility_months || 24;
  const t = useTick(renewalDate);
  const R = 34, C = 2 * Math.PI * R, pct = Math.min(1, (total - left) / total);

  if (ld.eligible) {
    return (
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3" data-testid="lucky-countdown-eligible">
        <PartyPopper size={20} className="text-emerald-300" />
        <p className="text-sm text-emerald-200">You're eligible for the promotional draw. Keep your subscription active to stay in.</p>
      </div>
    );
  }

  const qualifyBy = renewalDate && left > 0 ? addMonths(renewalDate, left - 1) : null;
  return (
    <div className="mt-4" data-testid="lucky-countdown">
      <div className="flex items-center gap-5">
        <div className="relative h-20 w-20 shrink-0">
          <svg viewBox="0 0 80 80" className="h-full w-full -rotate-90">
            <circle cx="40" cy="40" r={R} fill="none" stroke="rgba(229,193,88,0.12)" strokeWidth="6" />
            <circle cx="40" cy="40" r={R} fill="none" stroke="url(#ldg)" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C * (1 - pct)} style={{ transition: "stroke-dashoffset 1s ease" }} />
            <defs><linearGradient id="ldg"><stop offset="0" stopColor="#E5C158" /><stop offset="1" stopColor="#f6e3a1" /></linearGradient></defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-serif text-2xl text-amber-100 leading-none" data-testid="lucky-months-left">{left}</span>
            <span className="text-[9px] uppercase tracking-wider text-slate-400">months</span>
          </div>
        </div>
        <div className="min-w-0">
          <p className="text-sm text-slate-200"><span className="text-amber-200 font-semibold">{left} {left === 1 ? "month" : "months"}</span> to go until you qualify</p>
          {active && qualifyBy ? (
            <p className="mt-1 text-xs text-slate-400" data-testid="lucky-qualify-date">Estimated qualifying month: <span className="text-amber-200">{fmtMonth(qualifyBy)}</span></p>
          ) : (
            <p className="mt-1 text-xs text-slate-400">Activate your subscription to start the countdown.</p>
          )}
        </div>
      </div>
      {active && renewalDate && (
        <div className="mt-4 rounded-xl border border-amber-500/15 bg-[#0B1024]/60 px-4 py-3" data-testid="lucky-live-timer">
          <p className="text-[11px] uppercase tracking-wider text-amber-400/90 flex items-center gap-1.5"><Hourglass size={12} /> Next qualifying payment in</p>
          <div className="mt-2 grid grid-cols-4 gap-2 text-center">
            {[["Days", t.d], ["Hrs", t.h], ["Min", t.m], ["Sec", t.s]].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-amber-500/5 border border-amber-500/10 py-1.5">
                <p className="font-serif text-xl text-amber-100 tabular-nums" data-testid={`lucky-timer-${k.toLowerCase()}`}>{String(v).padStart(2, "0")}</p>
                <p className="text-[9px] uppercase tracking-wider text-slate-500">{k}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
